import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import { useI18n } from "../contexts/I18nContext";
import AdBanner from "./AdBanner";

const CATEGORIES = [
  { value: "", label: "Toutes", icon: "🔍" },
  { value: "soiree_privee", label: "Soirée privée", icon: "🎭" },
  { value: "club", label: "Club", icon: "🌙" },
  { value: "meetup", label: "Rencontre", icon: "🍷" },
  { value: "afterwork", label: "Afterwork", icon: "🥂" },
  { value: "sauna", label: "Sauna / Spa", icon: "♨️" },
  { value: "voyage", label: "Voyage", icon: "✈️" },
  { value: "weekend", label: "Weekend", icon: "🏖" },
  { value: "online", label: "En ligne", icon: "💻" },
  { value: "couple_seek", label: "Recherche couple", icon: "💑" },
  { value: "soft", label: "Soft / Découverte", icon: "🌸" },
  { value: "discussion", label: "Discussion", icon: "💬" },
  { value: "other", label: "Autre", icon: "✦" },
];

const WHO_LABELS = {
  all: "Ouvert à tou·te·s",
  couples_only: "Couples uniquement",
  men_only: "Hommes uniquement",
  women_only: "Femmes uniquement",
  couples_and_women: "Couples & femmes",
  verified_only: "Profils vérifiés",
};

function formatDate(d) {
  if (!d) return "Date flexible";
  return new Date(d).toLocaleDateString("fr-FR", {
    weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

export default function Encounters() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [encounters, setEncounters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [joining, setJoining] = useState(null);
  const [joinMessage, setJoinMessage] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [form, setForm] = useState({
    title: "", description: "", category: "meetup", who_can_join: "all",
    city: "", location_name: "", max_participants: 4, date_time: "",
    is_online: false, dress_code: "", rules: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCat) params.set("category", filterCat);
      const data = await api(`/api/encounters?${params}`);
      setEncounters(data.encounters || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterCat]);

  useEffect(() => { fetch_(); }, [fetch_]);

  async function handleCreate(e) {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      const body = { ...form, content: form.description, max_participants: parseInt(form.max_participants) || 4,
        date_time: form.date_time ? new Date(form.date_time).toISOString() : null };
      const data = await api("/api/encounters", { method: "POST", body: JSON.stringify(body) });
      setEncounters((prev) => [{ ...data.encounter, participant_count: 0 }, ...prev]);
      setShowForm(false);
      setForm({ title: "", description: "", category: "meetup", who_can_join: "all", city: "", location_name: "", max_participants: 4, date_time: "", is_online: false, dress_code: "", rules: "" });
      setSuccess("Proposition créée !"); setTimeout(() => setSuccess(""), 3000);
    } catch (err) { setError(err.data?.detail || err.message); }
    finally { setSubmitting(false); }
  }

  async function handleJoin(id) {
    try {
      await api(`/api/encounters/${id}/join`, { method: "POST", body: JSON.stringify({ message: joinMessage }) });
      setJoining(null); setJoinMessage("");
      setSuccess("Demande envoyée !"); setTimeout(() => setSuccess(""), 3000);
      fetch_();
    } catch (err) { setError(err.message); }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">{t("encounters_title")}</h2>
          <p className="mt-1 text-sm text-sensoria-text-dim">{t("encounters_desc")}</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="sensoria-btn px-4 py-2 text-sm">
          {showForm ? t("cancel") : t("encounters_propose")}
        </button>
      </div>

      {success && <div className="rounded-lg border border-sensoria-success/30 bg-sensoria-success/10 px-4 py-3 text-sm text-sensoria-success">{success}</div>}
      {error && <div className="rounded-lg border border-sensoria-danger/30 bg-sensoria-danger/10 px-4 py-3 text-sm text-sensoria-danger">{error} <button onClick={() => setError("")} className="ml-2 underline">fermer</button></div>}

      {showForm && (
        <form onSubmit={handleCreate} className="sensoria-card sensoria-glow space-y-4 animate-slide-up">
          <h3 className="font-display text-lg font-semibold text-white">{t("encounters_new")}</h3>
          <input type="text" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Titre (ex: Soirée libertine soft à Paris)" maxLength={150} required className="sensoria-input" />
          <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Décrivez l'événement, l'ambiance, vos attentes..." rows={4} maxLength={2000} required className="sensoria-input resize-none" />

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Catégorie</label>
              <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="sensoria-input appearance-none">
                {CATEGORIES.filter((c) => c.value).map((c) => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Qui peut rejoindre</label>
              <select value={form.who_can_join} onChange={(e) => setForm((f) => ({ ...f, who_can_join: e.target.value }))} className="sensoria-input appearance-none">
                {Object.entries(WHO_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Ville</label>
              <input type="text" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                placeholder={form.is_online ? "—" : "Ville"} disabled={form.is_online} className="sensoria-input" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Date & heure</label>
              <input type="datetime-local" value={form.date_time} onChange={(e) => setForm((f) => ({ ...f, date_time: e.target.value }))} className="sensoria-input" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Max participants</label>
              <input type="number" value={form.max_participants} onChange={(e) => setForm((f) => ({ ...f, max_participants: e.target.value }))}
                min={2} max={100} className="sensoria-input" />
            </div>
          </div>
          <input type="text" value={form.location_name} onChange={(e) => setForm((f) => ({ ...f, location_name: e.target.value }))}
            placeholder="Lieu (nom du club, adresse, lien...)" maxLength={120} className="sensoria-input" />
          <div className="grid gap-3 sm:grid-cols-2">
            <input type="text" value={form.dress_code} onChange={(e) => setForm((f) => ({ ...f, dress_code: e.target.value }))}
              placeholder="Dress code (optionnel)" maxLength={100} className="sensoria-input" />
            <label className="flex items-center gap-3 py-3">
              <input type="checkbox" checked={form.is_online} onChange={(e) => setForm((f) => ({ ...f, is_online: e.target.checked }))}
                className="h-4 w-4 rounded border-sensoria-border bg-sensoria-surface text-sensoria-accent" />
              <span className="text-sm text-sensoria-text-dim">En ligne</span>
            </label>
          </div>
          <textarea value={form.rules} onChange={(e) => setForm((f) => ({ ...f, rules: e.target.value }))}
            placeholder="Règles de l'événement (optionnel)" rows={2} maxLength={500} className="sensoria-input resize-none" />
          <button type="submit" disabled={submitting} className="sensoria-btn w-full">
            {submitting ? t("encounters_creating") : t("encounters_publish")}
          </button>
        </form>
      )}

      {/* Category filter */}
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button key={c.value} onClick={() => setFilterCat(c.value)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
              filterCat === c.value ? "border-sensoria-accent/50 bg-sensoria-accent/10 text-sensoria-accent-light" : "border-sensoria-border text-sensoria-text-dim hover:border-sensoria-muted"
            }`}>{c.icon} {c.label}</button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-2 border-sensoria-accent border-t-transparent" /></div>
      ) : encounters.length === 0 ? (
        <div className="py-16 text-center"><div className="mb-3 text-4xl">🔥</div><p className="text-sensoria-text-dim">{t("encounters_empty")}</p></div>
      ) : (
        <div className="space-y-4">
          {/* Encart pub en haut des rencontres */}
          <AdBanner variant="horizontal" showLabel />

          {encounters.map((enc, i) => {
            const cat = CATEGORIES.find((c) => c.value === enc.category) || CATEGORIES[0];
            const isAuthor = user?.id === enc.author_id;
            const isFull = enc.participant_count >= enc.max_participants - 1;
            return (
              <React.Fragment key={enc.id}>
              {i > 0 && i % 6 === 0 && <AdBanner variant="horizontal" />}
              <article className="sensoria-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{cat.icon}</span>
                      <h3 className="font-semibold text-white truncate">{enc.title}</h3>
                    </div>
                    <p className="mt-1 text-xs text-sensoria-text-dim">
                      par {enc.author_name} · {formatDate(enc.date_time)}
                    </p>
                  </div>
                  <div className="flex flex-shrink-0 flex-col items-end gap-1">
                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${
                      enc.status === "open" ? "bg-sensoria-success/15 text-sensoria-success" : "bg-sensoria-muted/15 text-sensoria-muted"
                    }`}>{enc.status === "open" ? "Ouvert" : enc.status}</span>
                    <span className="text-[10px] text-sensoria-text-dim">{enc.participant_count}/{enc.max_participants - 1} places</span>
                  </div>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-sensoria-text">{enc.description}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-sensoria-text-dim">
                  {enc.city && <span>📍 {enc.city}</span>}
                  {enc.location_name && <span>· {enc.location_name}</span>}
                  {enc.is_online && <span>💻 En ligne</span>}
                  {enc.dress_code && <span>· 👗 {enc.dress_code}</span>}
                  <span className="rounded-full bg-sensoria-surface px-2 py-0.5">{WHO_LABELS[enc.who_can_join] || enc.who_can_join}</span>
                </div>
                {enc.rules && <p className="mt-2 rounded-lg bg-sensoria-surface/50 p-2 text-xs text-sensoria-text-dim italic">📋 {enc.rules}</p>}

                {!isAuthor && enc.status === "open" && !isFull && (
                  <div className="mt-4 border-t border-sensoria-border/30 pt-3">
                    {joining === enc.id ? (
                      <div className="flex gap-2">
                        <input type="text" value={joinMessage} onChange={(e) => setJoinMessage(e.target.value)}
                          placeholder="Présentez-vous brièvement..." maxLength={500} className="sensoria-input flex-1 py-2 text-sm" />
                        <button onClick={() => handleJoin(enc.id)} className="sensoria-btn px-4 py-2 text-sm">Envoyer</button>
                        <button onClick={() => setJoining(null)} className="px-2 text-sensoria-muted hover:text-white">×</button>
                      </div>
                    ) : (
                      <button onClick={() => setJoining(enc.id)} className="text-sm font-medium text-sensoria-accent hover:text-sensoria-accent-light transition-colors">
                        {t("encounters_join")}
                      </button>
                    )}
                  </div>
                )}
              </article>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
